// Centralized language detection logic
// Used by: RegionContext, i18n, Zustand store

import { getLogger } from '@/lib/logger/logger';
import { REGION_STORAGE_KEYS } from '@/shared/constants/region';

const logger = getLogger('languageDetector');

interface RegionInfo {
  isOverseas?: boolean;
  countryCode?: string;
}

const getOverseasLang = (): 'en' | 'ja' => {
  const savedLang = localStorage.getItem(REGION_STORAGE_KEYS.I18N_LNG);
  return savedLang === 'ja' ? 'ja' : 'en';
};

/**
 * Detects the current language based on URL and region
 *
 * 4 Scenarios:
 * 1. 국내향 국문: Domestic IP + no /en in URL → Korean + Domestic products
 * 2. 국내향 영문: ANY IP + /en in URL → English + Domestic products
 * 3. 해외향 영문: Overseas IP + no /en in URL + no 'ja' saved → English + Overseas products
 * 4. 해외향 일문: Overseas IP + no /en in URL + 'ja' saved → Japanese + Overseas products
 *
 * Priority: URL /en prefix > region (test_region > region_cache) > fallback
 *
 * @param fallback Default language if no region info found (default: 'ko')
 * @returns Language code ('ko' | 'en' | 'ja')
 */
export const detectLanguageFromRegion = (fallback: 'ko' | 'en' = 'ko'): 'ko' | 'en' | 'ja' => {
  // ✅ 1. Check URL path - language prefix takes highest priority
  const path = window.location.pathname;
  if (path.startsWith('/en')) {
    logger.debug('[languageDetector] Using URL /en prefix → English');
    return 'en';
  }
  if (path.startsWith('/ja')) {
    logger.debug('[languageDetector] Using URL /ja prefix → Japanese');
    return 'ja';
  }

  // ✅ 2. URL doesn't have /en, so determine language by region
  // 2a. Check test region (testing mode)
  const testRegion = localStorage.getItem(REGION_STORAGE_KEYS.TEST);
  if (testRegion) {
    try {
      const regionInfo = JSON.parse(testRegion) as RegionInfo;
      if (regionInfo.isOverseas) {
        const lang = getOverseasLang();
        logger.debug('[languageDetector] Using test_region (overseas):', { regionInfo, lang });
        return lang;
      }
      logger.debug('[languageDetector] Using test_region (domestic):', { regionInfo });
      return 'ko';
    } catch (error) {
      logger.warn('[languageDetector] Invalid test_region JSON, ignoring:', error);
    }
  }

  // 2b. Check cached region (from actual IP detection)
  const cachedRegion = localStorage.getItem(REGION_STORAGE_KEYS.CACHE);
  if (cachedRegion) {
    try {
      const regionInfo = JSON.parse(cachedRegion) as RegionInfo;
      if (regionInfo.isOverseas) {
        const lang = getOverseasLang();
        logger.debug('[languageDetector] Using region_cache (overseas):', { regionInfo, lang });
        return lang;
      }
      logger.debug('[languageDetector] Using region_cache (domestic):', { regionInfo });
      return 'ko';
    } catch (error) {
      logger.warn('[languageDetector] Invalid region_cache JSON, ignoring:', error);
    }
  }

  // 3. Fallback
  logger.debug('[languageDetector] Using fallback:', { fallback });
  return fallback;
};

/**
 * Gets region info from storage
 * Priority: test_region > region_cache
 * @returns RegionInfo or null if not found
 */
export const getStoredRegionInfo = (): RegionInfo | null => {
  // Check test region first
  const testRegion = localStorage.getItem(REGION_STORAGE_KEYS.TEST);
  if (testRegion) {
    try {
      return JSON.parse(testRegion) as RegionInfo;
    } catch (error) {
      logger.warn('[languageDetector] Invalid test_region JSON:', error);
    }
  }

  // Check cached region
  const cachedRegion = localStorage.getItem(REGION_STORAGE_KEYS.CACHE);
  if (cachedRegion) {
    try {
      return JSON.parse(cachedRegion) as RegionInfo;
    } catch (error) {
      logger.warn('[languageDetector] Invalid region_cache JSON:', error);
    }
  }

  return null;
};
